/**
 * WizardStepsEditor - Editable steps list for wizard templates.
 * Allows reordering, adding, removing, and editing step configurations.
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  updateWizardTemplate,
  listWizardBlocks,
  type WizardTemplateStep,
  type WizardBlock,
} from "../../services/naraDoor";
import { KiiDialogLayout } from "../kii";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Loader2,
  Save,
  AlertCircle,
} from "lucide-react";

interface WizardStepsEditorProps {
  templateId: string;
  steps: WizardTemplateStep[];
  onSaved: () => void;
}

interface LocalStep {
  /** Original step id or empty for new steps */
  _key: string;
  block_id: string;
  sort_order: number;
  config_overrides: Record<string, any> | null;
  timeout_seconds: number | null;
  skippable: boolean;
  /** Whether the step row is expanded */
  _expanded: boolean;
}

const KNOWN_CONFIG_KEYS = ["title", "points", "achievementId", "celebrate", "showInEntryDetail"];

function stepToLocal(step: WizardTemplateStep, index: number): LocalStep {
  return {
    _key: step.id || `step-${index}`,
    block_id: step.block_id,
    sort_order: step.sort_order,
    config_overrides: step.config_overrides ? { ...step.config_overrides } : null,
    timeout_seconds: step.timeout_seconds,
    skippable: step.skippable,
    _expanded: false,
  };
}

export function WizardStepsEditor({ templateId, steps, onSaved }: WizardStepsEditorProps) {
  const [localSteps, setLocalSteps] = useState<LocalStep[]>(() =>
    steps.map((s, i) => stepToLocal(s, i))
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [blockSearch, setBlockSearch] = useState("");

  // Reset local steps when prop steps change (e.g. after save + refetch)
  useEffect(() => {
    setLocalSteps(steps.map((s, i) => stepToLocal(s, i)));
  }, [steps]);

  const { data: blocksData, isLoading: isBlocksLoading } = useQuery({
    queryKey: ["wizardBlocks"],
    queryFn: listWizardBlocks,
    enabled: showAddDialog,
  });

  const totalXp = localSteps.reduce(
    (sum, s) => sum + (s.config_overrides?.points || 0),
    0
  );

  // Dirty detection
  const isDirty = useMemo(() => {
    if (localSteps.length !== steps.length) return true;
    return localSteps.some((ls, i) => {
      const orig = steps[i];
      if (!orig) return true;
      if (ls.block_id !== orig.block_id) return true;
      if (ls.skippable !== orig.skippable) return true;
      if (ls.timeout_seconds !== orig.timeout_seconds) return true;
      if (JSON.stringify(ls.config_overrides) !== JSON.stringify(orig.config_overrides)) return true;
      return false;
    });
  }, [localSteps, steps]);

  const moveStep = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= localSteps.length) return;
    setLocalSteps((prev) => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const removeStep = (index: number) => {
    setLocalSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleExpand = (index: number) => {
    setLocalSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, _expanded: !s._expanded } : s))
    );
  };

  const updateStepField = (index: number, updates: Partial<LocalStep>) => {
    setLocalSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...updates } : s))
    );
  };

  const updateConfigOverride = (index: number, key: string, value: any) => {
    setLocalSteps((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const overrides = { ...(s.config_overrides || {}) };
        if (value === "" || value === undefined || value === null) {
          delete overrides[key];
        } else {
          overrides[key] = value;
        }
        return { ...s, config_overrides: Object.keys(overrides).length > 0 ? overrides : null };
      })
    );
  };

  const getOtherConfigJson = (step: LocalStep): string => {
    if (!step.config_overrides) return "";
    const other: Record<string, any> = {};
    for (const [k, v] of Object.entries(step.config_overrides)) {
      if (!KNOWN_CONFIG_KEYS.includes(k)) {
        other[k] = v;
      }
    }
    return Object.keys(other).length > 0 ? JSON.stringify(other, null, 2) : "";
  };

  const setOtherConfigJson = (index: number, json: string) => {
    setLocalSteps((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const overrides = { ...(s.config_overrides || {}) };
        // Remove all non-known keys
        for (const k of Object.keys(overrides)) {
          if (!KNOWN_CONFIG_KEYS.includes(k)) {
            delete overrides[k];
          }
        }
        // Parse and merge new ones
        if (json.trim()) {
          try {
            const parsed = JSON.parse(json);
            Object.assign(overrides, parsed);
          } catch {
            // Invalid JSON, keep as-is — user is still typing
          }
        }
        return { ...s, config_overrides: Object.keys(overrides).length > 0 ? overrides : null };
      })
    );
  };

  const addBlock = (block: WizardBlock) => {
    const newStep: LocalStep = {
      _key: `new-${Date.now()}-${Math.random()}`,
      block_id: block.block_id,
      sort_order: localSteps.length,
      config_overrides: null,
      timeout_seconds: null,
      skippable: false,
      _expanded: true,
    };
    setLocalSteps((prev) => [...prev, newStep]);
    setShowAddDialog(false);
    setBlockSearch("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateWizardTemplate(templateId, {
        steps: localSteps.map((s, i) => ({
          block_id: s.block_id,
          sort_order: i,
          config_overrides: s.config_overrides || undefined,
          timeout_seconds: s.timeout_seconds ?? undefined,
          skippable: s.skippable,
        })),
      });
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  // Group blocks by category for the add dialog
  const groupedBlocks = useMemo(() => {
    if (!blocksData?.blocks) return {};
    const filtered = blockSearch.trim()
      ? blocksData.blocks.filter(
          (b) =>
            b.label.toLowerCase().includes(blockSearch.toLowerCase()) ||
            b.block_id.toLowerCase().includes(blockSearch.toLowerCase()) ||
            b.category.toLowerCase().includes(blockSearch.toLowerCase())
        )
      : blocksData.blocks;
    const groups: Record<string, WizardBlock[]> = {};
    for (const block of filtered) {
      const cat = block.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(block);
    }
    return groups;
  }, [blocksData, blockSearch]);

  return (
    <>
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-semibold text-foreground">Steps</h4>
            {totalXp > 0 && (
              <span className="text-xs px-2 py-0.5 bg-nara-orange/10 text-nara-orange rounded-full font-medium">
                {totalXp} XP total
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isDirty && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => setShowAddDialog(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-nara-orange hover:bg-nara-orange/10 rounded-lg transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Step
            </button>
          </div>
        </div>

        {localSteps.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No steps yet. Click "Add Step" to get started.
          </div>
        )}

        <div className="space-y-2">
          {localSteps.map((step, index) => {
            const overrides = step.config_overrides || {};
            const title = overrides.title || step.block_id;
            const points = overrides.points || 0;

            return (
              <div
                key={step._key}
                className="border border-border rounded-lg overflow-hidden"
              >
                {/* Step row header */}
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleExpand(index)}
                >
                  <span className="text-xs font-mono text-muted-foreground w-6 text-center shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
                    {title}
                  </span>
                  {points > 0 && (
                    <span className="text-xs px-1.5 py-0.5 bg-nara-orange/10 text-nara-orange rounded font-medium shrink-0">
                      {points} XP
                    </span>
                  )}
                  {step.skippable && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded font-medium shrink-0">
                      skippable
                    </span>
                  )}
                  <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => moveStep(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => moveStep(index, 1)}
                      disabled={index === localSteps.length - 1}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeStep(index)}
                      className="p-1 text-muted-foreground hover:text-destructive"
                      title="Remove step"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {step._expanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </div>

                {/* Expanded step editor */}
                {step._expanded && (
                  <div className="px-3 py-3 space-y-3 border-t border-border bg-background">
                    <div className="text-xs text-muted-foreground font-mono">
                      Block: {step.block_id}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={overrides.title || ""}
                          onChange={(e) => updateConfigOverride(index, "title", e.target.value)}
                          placeholder="Step title"
                          className="w-full px-2.5 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Points / XP
                        </label>
                        <input
                          type="number"
                          value={overrides.points ?? ""}
                          onChange={(e) =>
                            updateConfigOverride(
                              index,
                              "points",
                              e.target.value ? parseInt(e.target.value, 10) : undefined
                            )
                          }
                          placeholder="0"
                          className="w-full px-2.5 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Achievement ID
                        </label>
                        <input
                          type="text"
                          value={overrides.achievementId || ""}
                          onChange={(e) => updateConfigOverride(index, "achievementId", e.target.value)}
                          placeholder="e.g. first-recording"
                          className="w-full px-2.5 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Timeout (seconds)
                        </label>
                        <input
                          type="number"
                          value={step.timeout_seconds ?? ""}
                          onChange={(e) =>
                            updateStepField(index, {
                              timeout_seconds: e.target.value ? parseInt(e.target.value, 10) : null,
                            })
                          }
                          placeholder="No timeout"
                          className="w-full px-2.5 py-1.5 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Max time (in seconds) the user has to complete this step before it auto-skips. Leave empty for no limit.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={overrides.celebrate ?? false}
                          onChange={(e) => updateConfigOverride(index, "celebrate", e.target.checked || undefined)}
                          className="h-4 w-4 rounded border-border"
                        />
                        Celebrate
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={step.skippable}
                          onChange={(e) => updateStepField(index, { skippable: e.target.checked })}
                          className="h-4 w-4 rounded border-border"
                        />
                        Skippable
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={overrides.showInEntryDetail ?? false}
                          onChange={(e) => updateConfigOverride(index, "showInEntryDetail", e.target.checked || undefined)}
                          className="h-4 w-4 rounded border-border"
                        />
                        Show in Entry Detail
                      </label>
                    </div>
                    {/* Other config overrides as JSON */}
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Other config overrides (JSON)
                      </label>
                      <textarea
                        value={getOtherConfigJson(step)}
                        onChange={(e) => {
                          setOtherConfigJson(index, e.target.value);
                          // Auto-resize to content
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        ref={(el) => {
                          // Initial auto-resize
                          if (el) {
                            el.style.height = "auto";
                            el.style.height = el.scrollHeight + "px";
                          }
                        }}
                        placeholder="{}"
                        rows={1}
                        className="w-full px-2.5 py-1.5 text-xs font-mono border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50 resize-none overflow-hidden"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Save button */}
        {localSteps.length > 0 && (
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Steps
            </button>
            {saveError && (
              <span className="text-sm text-destructive">{saveError}</span>
            )}
          </div>
        )}
      </div>

      {/* Add Step Dialog */}
      <KiiDialogLayout
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        title="Add Step"
        description="Choose a block to add as a new step"
        size="lg"
      >
        <div className="mt-4">
          <input
            type="text"
            value={blockSearch}
            onChange={(e) => setBlockSearch(e.target.value)}
            placeholder="Search blocks..."
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50 mb-4"
          />
          {isBlocksLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading blocks...
            </div>
          )}
          {!isBlocksLoading && Object.keys(groupedBlocks).length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No blocks found
            </p>
          )}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.entries(groupedBlocks).map(([category, blocks]) => (
              <div key={category}>
                <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {category}
                </h5>
                <div className="space-y-1">
                  {blocks.map((block) => (
                    <button
                      key={block.block_id}
                      onClick={() => addBlock(block)}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground">{block.label}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {block.block_id}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {block.ui_type}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </KiiDialogLayout>
    </>
  );
}
